import { useState } from "react";
import { TableHead } from "./table";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Button } from "./button";
import { Input } from "./input";
import { Checkbox } from "./checkbox";
import { Filter, X, ChevronDown } from "lucide-react";
import { cn } from "../../lib/utils";

export function FilterableTableHeader({
    children,
    filterKey,
    options = [],
    selectedValues = [],
    onFilterChange,
    onOpenChange, // Add this prop
    placeholder = "Filter...",
    searchPlaceholder = "Search...",
    className = ""
}) {
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    // Handle open change and notify parent
    const handleOpenChange = (newOpen) => {
        setOpen(newOpen);
        if (onOpenChange) {
            onOpenChange(filterKey, newOpen);
        }
    };

    const filteredOptions = options.filter((option) => {
        const label = typeof option === "string" ? option : (option.label || option.value || option);
        return label.toLowerCase().includes(searchTerm.toLowerCase());
    });

    const handleToggle = (value) => {
        const newSelected = selectedValues.includes(value)
            ? selectedValues.filter((v) => v !== value)
            : [...selectedValues, value];
        onFilterChange(filterKey, newSelected);
    };

    const handleSelectAll = () => {
        if (!filteredOptions.length) return;
        const allValues = Array.from(
            new Set(
                filteredOptions.map((option) =>
                    typeof option === "string"
                        ? option
                        : (option.value || option.label || option)
                )
            )
        );
        onFilterChange(filterKey, allValues);
    };

    const handleClear = () => {
        onFilterChange(filterKey, []);
        setOpen(false);
    };

    const hasActiveFilter = selectedValues.length > 0;

    return (
        <TableHead className={cn("relative", className)}>
            <Popover open={open} onOpenChange={handleOpenChange}>
                <PopoverTrigger asChild>
                    <button
                        className={cn(
                            "h-auto p-0 font-semibold hover:bg-transparent flex items-center gap-1.5 group transition-colors",
                            hasActiveFilter ? "text-blue-600" : "text-gray-700 hover:text-gray-900:text-gray-100"
                        )}
                    >
                        <span>{children}</span>
                        <div className="flex items-center gap-1">
                            {hasActiveFilter && (
                                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-semibold text-white bg-blue-600 rounded-full">
                                    {selectedValues.length}
                                </span>
                            )}
                            <ChevronDown className={cn(
                                "h-3.5 w-3.5 transition-all",
                                hasActiveFilter ? "text-blue-600" : "text-gray-400 group-hover:text-gray-600:text-gray-300"
                            )} />
                        </div>
                    </button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-0 shadow-lg border border-gray-200" align="start">
                    <div className="p-3 border-b bg-gray-50">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Filter by {children}</span>
                            <div className="flex items-center gap-1">
                                {filteredOptions.length > 0 && (
                                    <button
                                        onClick={handleSelectAll}
                                        className="h-6 px-2 text-xs text-gray-600 hover:text-gray-900:text-gray-100 hover:bg-gray-100:bg-gray-700 rounded transition-colors"
                                    >
                                        All
                                    </button>
                                )}
                                {hasActiveFilter && (
                                    <button
                                        onClick={handleClear}
                                        className="h-6 px-2 text-xs text-gray-600 hover:text-red-600:text-red-400 hover:bg-red-50:bg-red-900/20 rounded transition-colors flex items-center gap-1"
                                    >
                                        <X className="h-3 w-3" />
                                        Clear
                                    </button>
                                )}
                            </div>
                        </div>
                        <Input
                            placeholder={searchPlaceholder}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="h-8 text-sm"
                        />
                    </div>
                    <div className="max-h-60 overflow-y-auto p-1">
                        {filteredOptions.length === 0 ? (
                            <div className="text-xs text-gray-500 text-center py-6">
                                No options found
                            </div>
                        ) : (
                            <div className="space-y-0.5">
                                {filteredOptions.map((option) => {
                                    const value = typeof option === "string" ? option : (option.value || option.label || option);
                                    const label = typeof option === "string" ? option : (option.label || option.value || option);
                                    const isSelected = selectedValues.includes(value);

                                    return (
                                        <div
                                            key={value}
                                            className="flex items-center gap-2 px-2 py-1.5 hover:bg-blue-50:bg-gray-700 rounded cursor-pointer transition-colors"
                                            onClick={() => handleToggle(value)}
                                        >
                                            <Checkbox
                                                checked={isSelected}
                                                onCheckedChange={() => handleToggle(value)}
                                            />
                                            <span className="text-sm flex-1 text-gray-700">{label}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    {hasActiveFilter && (
                        <div className="p-2 border-t bg-gray-50">
                            <div className="text-xs font-medium text-gray-700">
                                {selectedValues.length} selected
                            </div>
                        </div>
                    )}
                </PopoverContent>
            </Popover>
        </TableHead>
    );
}

